import type { EventTrackingContext } from 'providers/EventTrackingProvider';
import type { Lead, LeadContext } from 'providers/LeadProvider';
import type { GrowthBookApp } from 'services/growthbook';
import { type Country, resolveCountry } from 'services/i18n';
import { type RumService, rumService } from 'services/rum';
import type { RequireNonNull } from 'typings/utils';
import {
  type ConversionEvents,
  type GrowthBookAttributes,
} from 'utils/experimentation/data';

/**
 * Type guard to check if a given lead object has 'id' attributes.
 *
 * @param lead - The lead object to check.
 * @returns True if the lead has the 'id' property, otherwise false.
 */
function hasLeadId(lead?: Lead): lead is RequireNonNull<Lead, 'id'> {
  return Boolean(lead?.id);
}

/**
 * Updates GrowthBook attributes by merging new attributes with existing ones.
 *
 * Under Remote Evaluation this is a network round trip (`POST /api/eval`), so
 * a failure is a normal, expected outcome, not exceptional: every caller in
 * this app degrades to GrowthBook's last-known value rather than blocking on
 * it, so the reporting lives here once instead of a `.catch` at every call
 * site. `rum` defaults to the app-wide `rumService` singleton but can be
 * overridden by callers that need a DI'd instance for testability (e.g. a
 * route loader's injected `context.rum`).
 */
export async function updateAttributes({
  growthbook,
  attributes,
  source = 'update_attributes',
  rum = rumService,
}: {
  growthbook: GrowthBookApp;
  attributes: Partial<GrowthBookAttributes>;
  source?: string;
  rum?: RumService;
}) {
  try {
    await growthbook.setAttributes({
      ...growthbook.getAttributes(),
      ...attributes,
    });
  } catch (error) {
    rum.addError(error, { source });
  }
}

/**
 * Corrects GrowthBook's lead-experiment attributes (lead_id, business_type,
 * country, country_code, lead_created_at) against the given lead. GrowthBook's
 * in-memory attribute state does not survive a full page load, so a session
 * landing mid-flow via reload, a direct/bookmarked URL, or `/email-validation`
 * recovery would otherwise run with stale or absent attributes for its whole
 * duration.
 *
 * No-ops unless a derived value actually diverges from what GrowthBook
 * already holds: under Remote Evaluation, `updateAttributes` is a network
 * round trip, so this must not fire unconditionally on every navigation. The
 * comparison is per-key (not a whole-object equality check), so it can never
 * report a mismatch just because the two sides happen to have a different
 * number of keys.
 *
 * lead_created_at is included in the derived bundle only when the lead
 * actually has one: a lead cache momentarily missing createdAt (e.g. before
 * this session's first sync) must not be treated as "different" from a
 * GrowthBook value that's already correct — that would fire a needless
 * update that overwrites the good value with undefined.
 */
export async function setLeadExperimentAttributes({
  growthbook,
  lead,
  rum,
}: {
  growthbook: GrowthBookApp;
  lead: LeadContext['lead'];
  rum?: RumService;
}) {
  if (!hasLeadId(lead)) {
    return;
  }

  const country = lead.country ?? resolveCountry();
  const attributes: Partial<GrowthBookAttributes> = {
    lead_id: lead.id,
    business_type: lead.businessType,
    country,
    country_code: country.toUpperCase() as Uppercase<Country>,
    ...(lead.createdAt !== undefined
      ? { lead_created_at: lead.createdAt }
      : {}),
  };

  const current = growthbook.getAttributes();
  const isInSync = (
    Object.keys(attributes) as (keyof typeof attributes)[]
  ).every(key => current[key] === attributes[key]);

  if (isInSync) {
    return;
  }

  await updateAttributes({
    growthbook,
    attributes,
    source: 'before_load_signup_sub_route',
    rum,
  });
}

/**
 * Tracks conversion events by recording them with the provided tracking context.
 *
 * @param params - The tracking parameters
 * @param params.eventName - The name of the conversion event to track
 * @param params.snowplowRecord - The record function from the event tracking context used to log the event
 * @param params.contextData - Additional context data to be included with the event tracking
 *
 * @example
 * ```typescript
 * trackConversionEvents({
 *   eventName: 'user_signup',
 *   snowplowRecord: trackingContext.record,
 *   contextData: { userId: '123', source: 'homepage' }
 * });
 * ```
 */
export function trackConversionEvents({
  eventName,
  snowplowRecord: record,
  contextData,
}: {
  eventName: ConversionEvents;
  snowplowRecord: EventTrackingContext['record'];
  contextData: Record<string, unknown>;
}) {
  record({
    action: eventName,
    contextData,
  });
}
